import React, { useEffect, useState, useMemo, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { PatientSelfDataContext } from '../../../../context/PatientSelfDataContext';
import { db, auth, functions } from '../../../../firebase';
import { doc, runTransaction, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ExclamationIcon } from '@heroicons/react/solid';
import { updateDataModelWithPrefill } from '../functions/PrefillFunctions';
import { scoreScale, isScorableScale } from '../functions/ScalesFunctions';
import { checkAndNotifyAllQuestionnairesComplete, checkAndNotifySelfHarmConcern } from '../functions/NotificationFunctions';

import FormNavigation from './FormNavigation';
import FormCompletionCard from './FormCompletionCard';
import FormLockedCard from './FormLockedCard';

import FormAnswerSummary from './FormAnswerSummary';
import FormCurrentQuestionPanel from './FormCurrentQuestionPanel';
import FormProgressBar from './FormProgressBar';
import AllFormsCompletedFeedbackModal from './AllFormsCompletedFeedbackModal';
import SelfHarmCrisisSupportDialog from './SelfHarmCrisisSupportDialog';
import { isSlamNhsOrg } from '../../../providers/2_dashboard/config/patientSetupConfig';
import { buildEnhancedFormWithSubmission, recomputeProgress, clampPercentage, isQuestionAnswered } from '../../../../utils/formProgress';
import { computeIsFormLocked } from '../../../../utils/formLockingUtils';
import { buildAnswerLookup, isSubquestionVisible, getVisibleSubquestionEntries, extractPrimaryValue, unwrapNestedValue, normalizeSelectMultipleSelections } from '../../../../utils/formVisibility';
import { otherOptionFieldKey } from '../utils/sanitizeOptionForKey';
import { getDeviceContext } from '../../../../utils/getDeviceContext';
import { emitFormStarted, emitFormSaved, emitFormSaveFailed, emitFormCompleted, emitAllFormsCompleted, roleFromUserType, assessmentTypeFromModule } from '../../../../analytics/serviceUserFormEvents';

// Helper function to find partially completed questions for BDEFS/WEISS
const findPartiallyCompletedQuestion = (formData, panels, formKey) => {
    const requiresCompleteValidation = formKey === 'bdefs' || formKey === 'weiss';
    
    if (!requiresCompleteValidation || !formData?.formSubsections || !panels) {
        return null;
    }

    // Look through all questions to find partially completed ones
    for (const [panelIndex, panel] of Object.entries(panels)) {
        for (const [questionIndex] of Object.entries(panel.questions || {})) {
            const panelIdx = parseInt(panelIndex);
            const questionIdx = parseInt(questionIndex);
            
            // Get the question data from Firestore
            const questionData = formData.formSubsections?.[panelIdx]?.questions?.[questionIdx];
            
            if (questionData?.subquestions) {
                let answeredCount = 0;
                let totalCount = 0;
                
                // Count answered vs total subquestions
                Object.values(questionData.subquestions).forEach(subquestion => {
                    totalCount++;
                    if (subquestion.answer !== undefined && 
                        subquestion.answer !== null && 
                        subquestion.answer !== "No answer given") {
                        answeredCount++;
                    }
                });
                
                // If partially completed (some but not all answered), return this question
                if (answeredCount > 0 && answeredCount < totalCount) {
                    console.log(`[PatientFormTemplate] Found partially completed question: Panel ${panelIdx}, Question ${questionIdx} (${answeredCount}/${totalCount} answered)`);
                    return { panelIndex: panelIdx, questionIndex: questionIdx };
                }
            }
        }
    }
    
    return null; // No partially completed questions found
};

export default function PatientFormTemplate({ formTitle, formKey: explicitFormKey }) {
    const navigate = useNavigate();
    const { register, handleSubmit, reset, setValue, watch, control, getValues } = useForm();
    const { patientDataModel } = useContext(PatientSelfDataContext);

    const [userType, setUserType] = useState(null);

    useEffect(() => {
        const getUserType = async () => {
            const user = auth.currentUser;
            if (user) {
                const token = await user.getIdTokenResult();
                setUserType(token.claims.userType);
            }
        };
        getUserType();
    }, []);

    // Context for the service-user form-filling analytics milestones.
    // Claims come from the cached ID token (never a force refresh) because
    // the local userType state may not have resolved yet when the
    // first-access effect fires. Never throws; returns null when unavailable.
    const getFormEventContext = useCallback(async (eventFormKey) => {
        try {
            const user = auth.currentUser;
            if (!user) return null;
            const token = await user.getIdTokenResult();
            const role = roleFromUserType(token.claims.userType);
            // An ADHD informant's own doc lives in patients/{informantUid} and
            // records the patient they answer about in createdBy. The events
            // describe that patient, so one journey reads as one subject;
            // the informant remains the event's actor.
            const subjectId =
                role === 'informant'
                    ? patientDataModel?.createdBy || user.uid
                    : user.uid;
            return {
                patientId: subjectId,
                formKey: eventFormKey,
                role,
                // ADHD accounts predate the assessmentModule claim (only
                // autism-era accounts carry it) - default like the feedback
                // modal and the backend notification service do.
                assessmentType: assessmentTypeFromModule(token.claims.assessmentModule || 'adultAdhd'),
            };
        } catch (_) {
            return null;
        }
        // createdBy is read above, so it must be a dependency: with an empty
        // list the callback would close over the first render's value and an
        // informant's events would keep naming the wrong subject.
    }, [patientDataModel?.createdBy]);

    // Form state
    const [formState, setFormState] = useState({
        currentPanel: 1,
        currentQuestion: 1,
        isEditing: false,
        editIndex: null,
        lastUnanswered: { panelIndex: 1, questionIndex: 1 },
        showOtherInput: false,
        showConfetti: false,
        submittedAnswers: [],
        originalAnswers: null,
        seenQuestions: {},
    });

    // Add state for tracking locked status
    const [isFormLocked, setIsFormLocked] = useState(false);

    // Tracks whether a save-and-navigate operation is in flight so the UI can
    // disable the button and show a spinner.  On slow networks (3G) the
    // Firestore transaction can take several seconds, and without visual
    // feedback the user may think the app is frozen or tap the button again.
    const [isSaving, setIsSaving] = useState(false);

    // Gates the form content behind a skeleton loader until initializeFormState
    // resolves with fresh Firestore data. Without this, on page refresh the
    // component renders immediately from stale localStorage cache — which can
    // briefly show a "completed" state before the async getDoc returns the
    // actual next-unanswered position. The skeleton matches the form's visual
    // structure (progress bar + question card) so the layout doesn't shift.
    const [isInitializing, setIsInitializing] = useState(true);
    
    // Add state for feedback modal
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    // When we fetch a fresh patient document inside initializeFormState(), we compute
    // a "fresh from Firestore" progress value and store it here. The progress bar
    // will prefer this value over any potentially stale value held in context.
    // We still allow the displayed progress to increase beyond this value by taking
    // the max with any locally computed progress so the UI remains responsive after
    // the first paint.
    const [freshProgressOverride, setFreshProgressOverride] = useState(null);
    // We only want to show confetti once the user has actively confirmed/submitted
    // the final question at least once, even if provider prefill has already pushed
    // the progress to 100%. This avoids surprising the user with celebration before
    // they have reviewed and accepted the last answer.
    const [hasSubmittedFinalQuestionOnce, setHasSubmittedFinalQuestionOnce] = useState(false);

    // Controls the crisis-support alert dialog shown to SLaM / 3 Borough
    // patients after they submit a concerning PHQ-9 Q9 answer. The dialog
    // blocks interaction with the form until the patient acknowledges the
    // resources by clicking the proceed button. The email notification fires
    // independently — this is purely a presentational gate.
    const [showCrisisSupportDialog, setShowCrisisSupportDialog] = useState(false);

    // Tracks which form key has been fully initialized from Firestore so
    // that the initializeFormState effect runs exactly once per form.
    // Without this guard, every onSnapshot event from PatientSelfDataContext
    // creates new useMemo references for panels/panelKeys, re-triggering
    // initializeFormState which calls setFormState (rebuilding submittedAnswers)
    // and ultimately reset() on React Hook Form -- wiping any unsaved typing.
    // The ref is set to the form key only AFTER a successful initialization so
    // that a failed attempt can retry on the next effect cycle.
    const hasInitializedRef = useRef(null);

    // Tracks a stable identity for the current question so the prefill useEffect
    // can distinguish a genuine question transition (user clicked Save & Continue)
    // from a spurious re-render caused by patientDataModel getting a new object
    // reference (e.g. from fetchPatientData or the debounced queue). Without this
    // guard, reset() wipes any in-progress typing whenever the useMemo chain
    // produces a new currentQuestion reference with identical content.
    const prevPrefillKeyRef = useRef(null);

    // Accessibility: ref for the current question heading (used to move focus on
    // question transitions) and a ref for the live region that announces the
    // current question to screen readers.
    const questionHeadingRef = useRef(null);
    const liveRegionRef = useRef(null);

    // When the current question changes, move focus to the heading and announce
    // the transition via the aria-live region (ported from the v2 useAccessibility hook).
    const announceQuestion = useCallback((text) => {
        const el = liveRegionRef.current;
        if (!el) return;
        el.textContent = '';
        setTimeout(() => { el.textContent = text; }, 50);
    }, []);

    useEffect(() => {
        if (!formState.currentQuestion || !formState.currentPanel) return;
        // Small delay so the DOM has time to render the new question content.
        const id = setTimeout(() => {
            const heading = questionHeadingRef.current;
            if (heading && typeof heading.focus === 'function') {
                heading.focus({ preventScroll: true });
            }
            // Announce the transition to screen readers via the live region.
            if (heading) {
                const title = heading.textContent || '';
                announceQuestion(title);
            }
        }, 100);
        return () => clearTimeout(id);
    }, [formState.currentPanel, formState.currentQuestion, announceQuestion]);

    // Scroll so the user lands on the subsection title (which shows the
    // question number), giving full context of the question they are on/editing.
    // We fall back through the previous targets if the heading is not present.
    const scrollToFormTop = () => {
        setTimeout(() => {
            const sectionHeading = document.getElementById('form-section-heading');
            if (sectionHeading) {
                sectionHeading.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
                return;
            }
            const navigationContainer = document.getElementById('form-navigation-container');
            if (navigationContainer) {
                // Scroll to navigation with some padding to ensure buttons are visible
                navigationContainer.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            } else {
                // Fallback to progress bar if navigation not found
                const progressBar = document.getElementById('form-progress-bar-container');
                if (progressBar) {
                    progressBar.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                } else {
                    // Final fallback to top of page
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }
            }
        }, 100);
    };


    // Get form data from context
    const allFormData = useMemo(() =>
        Object.entries(patientDataModel?.formData || {}).map(([key, form]) => ({
            key,
            ...form
        })),
        [patientDataModel]
    );

    const currentFormData = useMemo(() => {
        // Prefer formKey when provided to avoid collisions on identical titles
        if (explicitFormKey) {
            const byKey = allFormData.find(form => form.key === explicitFormKey);
            if (byKey) return byKey;
        }
        // Fallback: locate by title (backward compatibility)
        return allFormData.find(form => form.formTitle === formTitle);
    }, [allFormData, formTitle, explicitFormKey]);

    // Calculate form structure and progress
    const panels = useMemo(() => currentFormData?.formSubsections || {}, [currentFormData]);

  // Sorted list of available panel indices (as numbers)
  const panelKeys = useMemo(() => (
    Object.keys(panels || {})
      .map(k => parseInt(k, 10))
      .filter(n => !Number.isNaN(n))
      .sort((a, b) => a - b)
  ), [panels]);

    // Get current panel and question - moved up to be used in hooks
    const currentPanel = useMemo(() =>
        panels?.[formState.currentPanel] || null,
        [panels, formState.currentPanel]
    );

    const currentQuestion = useMemo(() =>
        currentPanel?.questions?.[formState.currentQuestion] || null,
        [currentPanel, formState.currentQuestion]
    );

  // Determine if the user is currently viewing the very last question in the form.
  // We use this to gate celebratory UI (confetti) until they have confirmed/submitted
  // the final question at least once in this session.
  const isOnLastQuestion = useMemo(() => {
    if (!panels || panelKeys.length === 0) return false;
    const lastPanelIndex = panelKeys[panelKeys.length - 1];
    const lastQuestionKeys = Object.keys(panels[lastPanelIndex]?.questions || {})
      .map(k => parseInt(k, 10))
      .filter(n => !Number.isNaN(n))
      .sort((a, b) => a - b);
    const lastQuestionIndex = lastQuestionKeys[lastQuestionKeys.length - 1] || 1;
    return formState.currentPanel === lastPanelIndex && formState.currentQuestion === lastQuestionIndex;
  }, [panels, panelKeys, formState.currentPanel, formState.currentQuestion]);

    // ── Name-change detection ────────────────────────────────────────────
    // When the patient is on the "Your Name" question in the yourInformation
    // (or yourInformationSubclinical) form, we detect whether they've changed
    // their firstName or lastName compared to the root patient document fields.
    // If so, an info banner appears and a fire-and-forget CF call fires on save
    // to propagate the name change across all denormalized Firestore locations.
    // Changes to middleName (subquestion 3) are intentionally ignored — it is
    // not used in displayName, clinician docs, informant docs, or summaries.
    const isNameQuestion = useMemo(() => {
        const formKey = currentFormData?.key;
        return (
            (formKey === 'yourInformation' || formKey === 'yourInformationSubclinical') &&
            formState.currentPanel === 1 &&
            formState.currentQuestion === 1
        );
    }, [currentFormData?.key, formState.currentPanel, formState.currentQuestion]);

    const firstNameKey = `question_${formState.currentPanel}_${formState.currentQuestion}_2`;
    const lastNameKey = `question_${formState.currentPanel}_${formState.currentQuestion}_4`;
    const watchedFirst = watch(firstNameKey);
    const watchedLast = watch(lastNameKey);

    const nameChangeInfo = useMemo(() => {
        if (!isNameQuestion || !patientDataModel) {
            return { hasChanged: false, effectiveFirst: '', effectiveLast: '' };
        }

        const docFirst = patientDataModel.firstName || '';
        const docLast = patientDataModel.lastName || '';
        const firstChanged = watchedFirst != null && watchedFirst !== '' && watchedFirst !== docFirst;
        const lastChanged = watchedLast != null && watchedLast !== '' && watchedLast !== docLast;

        return {
            hasChanged: firstChanged || lastChanged,
            effectiveFirst: firstChanged ? watchedFirst : docFirst,
            effectiveLast: lastChanged ? watchedLast : docLast,
        };
    }, [isNameQuestion, watchedFirst, watchedLast, patientDataModel]);

  // If the configured currentPanel doesn't exist (e.g., subsection '1' was removed),
  // move to the first available panel and its first question to avoid blank screens.
  useEffect(() => {
    if (!currentFormData) return;
    if (!panelKeys || panelKeys.length === 0) return;
    if (!panels?.[formState.currentPanel]) {
      const firstPanel = panelKeys[0];
      const firstQuestionKey = Object.keys(panels[firstPanel]?.questions || {})
        .map(k => parseInt(k, 10))
        .filter(n => !Number.isNaN(n))
        .sort((a, b) => a - b)[0] || 1;
      setFormState(prev => ({
        ...prev,
        currentPanel: firstPanel,
        currentQuestion: firstQuestionKey,
        lastUnanswered: { panelIndex: firstPanel, questionIndex: firstQuestionKey },
      }));
    }
  }, [currentFormData, panelKeys, panels, formState.currentPanel]);

    // Calculate the global question number
    const currentQuestionNumber = useMemo(() => {
        // Count questions in previous panels
        let questionCount = 0;
        const panelKeys = Object.keys(panels).map(k => parseInt(k, 10)).sort((a, b) => a - b);

        for (let i = 0; i < panelKeys.length; i++) {
            const panelIdx = panelKeys[i];

            // If we've reached the current panel, add the current question index and break
            if (panelIdx === formState.currentPanel) {
                questionCount += formState.currentQuestion;
                break;
            }

            // Otherwise add all questions from this panel
            questionCount += Object.keys(panels[panelIdx].questions || {}).length;
        }

        return questionCount;
    }, [panels, formState.currentPanel, formState.currentQuestion]);


    const totalQuestions = useMemo(() =>
        Object.values(panels || {}).reduce((total, panel) =>
            total + Object.keys(panel.questions || {}).length,
            0),
        [panels]
    );

    // Reconstruct the form structure by merging panels (the static form definition)
    // with submittedAnswers (the in-memory state updated after each question save).
    // This gives us a formSubsections-shaped object that recomputeProgress and the
    // completion card check can both consume with identical visibility-aware logic,
    // ensuring progress %, confetti, completion card, and submit button always agree.
    const reconstructedFormData = useMemo(() => {
        const formSubsections = {};
        Object.entries(panels).forEach(([panelIndex, panel]) => {
            const questions = {};
            Object.entries(panel.questions || {}).forEach(([questionIndex, question]) => {
                const entry = formState.submittedAnswers.find(e =>
                    e.questionVarName === question.questionVarName
                );
                const subquestions = {};
                Object.entries(question.subquestions || {}).forEach(([sqIdx, sq]) => {
                    const answerEntry = entry?.answers?.[parseInt(sqIdx) - 1];
                    subquestions[sqIdx] = {
                        ...sq,
                        answer: answerEntry?.answer ?? sq?.answer ?? null,
                    };
                });
                questions[questionIndex] = {
                    ...question,
                    subquestions,
                    wasSkipped: entry?.wasSkipped ?? question.wasSkipped ?? false,
                };
            });
            formSubsections[panelIndex] = { questions };
        });
        return { formSubsections };
    }, [formState.submittedAnswers, panels]);

    const progress = useMemo(() => {
        // Progress display order of preference (highest wins):
        // 1) Fresh value derived from the most recent Firestore snapshot we fetched on mount
        // 2) Server-stored value from context (may be stale during initial render)
        // 3) Local computation from reconstructedFormData via recomputeProgress
        //
        // All three sources use the same every() + visibility-aware logic via
        // recomputeProgress / isQuestionAnswered, so they agree on semantics.
        // We pick the MAX so the bar never lags behind a fresher source,
        // and we clamp to [0, 100] for safety.
        const fresh = freshProgressOverride;
        const server = currentFormData?.progress?.progressPercentage;
        const local = recomputeProgress(reconstructedFormData)?.progressPercentage ?? 0;
        const displayed = Math.max(
            typeof fresh === 'number' ? fresh : 0,
            typeof server === 'number' ? server : 0,
            typeof local === 'number' ? local : 0
        );
        return clampPercentage(displayed);
    }, [freshProgressOverride, currentFormData?.progress?.progressPercentage, reconstructedFormData]);

    // Visibility-aware completion check: the form is complete when every question
    // has all visible subquestions answered (or the question was skipped). Reuses
    // the shared reconstructedFormData so this always agrees with the progress bar.
    const shouldShowCompletionCard = useMemo(() => {
        const answerLookup = buildAnswerLookup(reconstructedFormData.formSubsections);

        return Object.entries(panels).every(([panelIndex, panel]) => {
            return Object.entries(panel.questions || {}).every(([questionIndex, question]) => {
                const entry = formState.submittedAnswers.find(e =>
                    e.questionVarName === question.questionVarName
                );
                if (entry?.wasSkipped === true) return true;

                const reconstructedQ = reconstructedFormData.formSubsections[panelIndex]?.questions?.[questionIndex];
                const visibleEntries = getVisibleSubquestionEntries(reconstructedQ, answerLookup);
                if (visibleEntries.length === 0) return true;

                return isQuestionAnswered(reconstructedQ, visibleEntries);
            });
        });
    }, [formState.submittedAnswers, panels, reconstructedFormData]);

    // Cross-question answer lookup for subquestion-level conditionsToShow.
    // Today no Adult ADHD form has cross-question conditions, but threading
    // this through provides defensive coverage — if a future form definition
    // adds one, the subquestion will render correctly without a code change.
    // buildAnswerLookup already applies extractPrimaryValue to each value.
    const storedAnswerLookup = useMemo(
        () => buildAnswerLookup(reconstructedFormData?.formSubsections),
        [reconstructedFormData],
    );

    // Initialize form from Firestore.
    // Runs once per form key; subsequent onSnapshot-driven re-renders are
    // skipped via hasInitializedRef to prevent wiping unsaved user input.
    useEffect(() => {
        async function initializeFormState() {
            const formKey = currentFormData?.key;
            const user = auth.currentUser;
            if (!user || !formKey) {
                setIsInitializing(false);
                return;
            }

            if (hasInitializedRef.current === formKey) {
                console.debug(`[PatientFormTemplate] Skipping re-init for "${formKey}" (already initialized)`);
                setIsInitializing(false);
                return;
            }
            console.log("Initializing form state")
            try {
                const patientRef = doc(db, 'patients', user.uid);
                const patientDoc = await getDoc(patientRef);

                if (!patientDoc.exists()) {
                    setIsInitializing(false);
                    return;
                }

                const formData = patientDoc.data().formData?.[formKey];
                if (!formData) {
                    setIsInitializing(false);
                    return;
                }

                // Compute a fresh progress value from the just-fetched Firestore doc
                // and store it as a local override for the progress bar. This ensures
                // the bar reflects the latest persisted state even if the context value
                // is temporarily stale.
                try {
                    const recomputed = recomputeProgress(formData)?.progressPercentage ?? 0;
                    const snapshotProgress = formData?.progress?.progressPercentage ?? 0;
                    const preferred = Math.max(snapshotProgress, recomputed);
                    setFreshProgressOverride(clampPercentage(preferred));
                } catch (e) {
                    // If anything goes wrong, do not block the UI; let other sources drive progress.
                    console.error("[PatientFormTemplate] Failed to compute fresh progress override", e);
                    setFreshProgressOverride(null);
                }

                // Record the first-access timestamp and device context as a
                // best-effort metadata write.  Uses updateDoc instead of
                // runTransaction to avoid contention with saveToFirebase — both
                // would otherwise hold optimistic locks on the same patient
                // document, causing a retry storm on slow networks.  The guard
                // above (`!formData.firstStartedAt`) already confirmed the
                // field is unset; worst case a second tab sets it again
                // (harmless — same server timestamp).
                const isFirstTimeAccess = !formData.firstStartedAt;
                if (isFirstTimeAccess) {
                    try {
                        console.log(`[PatientFormTemplate] First time accessing form ${formKey}, setting firstStartedAt timestamp`);
                        const deviceCtx = getDeviceContext();

                        const updates = {
                            [`formData.${formKey}.firstStartedAt`]: serverTimestamp(),
                        };
                        if (deviceCtx) {
                            updates[`formData.${formKey}.deviceContext`] = deviceCtx;
                        }
                        await updateDoc(patientRef, updates);
                    } catch (e) {
                        console.warn(`[PatientFormTemplate] firstStartedAt write failed (non-blocking):`, e);
                    }
                    // Milestone analytics: first access to this form.
                    getFormEventContext(formKey).then((ctx) => {
                        if (ctx) emitFormStarted(ctx);
                    });
                }

                // Check for prefilled questions from provider
                const prefillData = {};

                // Scan for prefilled fields (but don't treat them as answered)
                Object.entries(formData.formSubsections || {}).forEach(([pIndex, panel]) => {
                    Object.entries(panel.questions || {}).forEach(([qIndex, question]) => {
                        const hasPrefill = question.subquestions && 
                            Object.values(question.subquestions).some(sq => 
                                sq.answer !== undefined && 
                                sq.answer !== null && 
                                sq.answer !== "No answer given"
                            );
                            
                        if (hasPrefill) {
                            prefillData[`${pIndex}_${qIndex}`] = true;
                        }
                    });
                });
                
                // Initialize answers array and tracking
                const answers = [];
                let seenQuestionTracking = {};
                let panelIndex = 1;
                let questionIndex = 1;

                // If there's saved progress, use it for navigation
                const savedProgress = formData.progress;
                if (savedProgress) {
                    // For BDEFS/WEISS: Check for partially completed questions first
                    const partiallyCompleted = findPartiallyCompletedQuestion(formData, panels, currentFormData?.key);
                    
                    if (partiallyCompleted) {
                        // Use partially completed question
                        panelIndex = partiallyCompleted.panelIndex;
                        questionIndex = partiallyCompleted.questionIndex;
                        console.log(`[PatientFormTemplate] Initializing to partially completed question: Panel ${panelIndex}, Question ${questionIndex}`);
                    } else {
                        // Fall back to normal lastUnanswered logic
                    // Prefer the saved panel index if it exists; otherwise use first available
                    panelIndex = panels?.[savedProgress.lastUnanswered.panelIndex]
                      ? savedProgress.lastUnanswered.panelIndex
                      : (panelKeys[0] || 1);

                    const availableQuestionKeys = Object.keys(panels?.[panelIndex]?.questions || {})
                      .map(k => parseInt(k, 10))
                      .filter(n => !Number.isNaN(n))
                      .sort((a, b) => a - b);
                    const fallbackQuestion = availableQuestionKeys[0] || 1;
                    const requestedQuestion = savedProgress.lastUnanswered.questionIndex;
                    questionIndex = availableQuestionKeys.includes(requestedQuestion) ? requestedQuestion : fallbackQuestion;
                    }

                    // Get tracking of seen questions
                    seenQuestionTracking = savedProgress.seenQuestions || {};
                }

                // Process all questions for answers, including prefilled ones
                // (This now happens regardless of whether progress exists)
                Object.entries(formData.formSubsections || {}).forEach(([pIndex, panel]) => {
                    Object.entries(panel.questions || {}).forEach(([qIndex, question]) => {
                        const questionKey = `${pIndex}_${qIndex}`;

                        // Consider a question "seen" ONLY IF:
        // 1. It's explicitly marked as seen by the question data
        // 2. It's in the seenQuestions tracking
        // Do NOT include questions that merely have prefill data
        if (question.hasSeenQuestion || seenQuestionTracking[questionKey]) {
            if (question.subquestions) {
                const subquestions = Object.entries(question.subquestions).map(([subIndex, subquestion]) => ({
                    question: subquestion.subquestionDisplayText,
                    answer: subquestion.answer ?? "No answer given",
                    serviceUserVarName: subquestion.serviceUserVarName || null,
                }));

                // Check if this question was skipped
                const wasSkipped = question.wasSkipped === true;
                
                // If the question was skipped, use the skipButtonText for display
                const skipText = question.skipButtonText || "I'm unable to provide this information";
                const displayAnswers = wasSkipped
                ? Object.values(question.subquestions).map(sq => ({
                    question: sq.subquestionDisplayText,
                    answer: skipText,
                    serviceUserVarName: sq.serviceUserVarName || null,
                })) 
                : subquestions;

                answers.push({
                    questionTitle: question.questionTitle,
                    questionVarName: question.questionVarName,
                    answers: wasSkipped ? displayAnswers : subquestions,
                    wasSkipped: wasSkipped,
                    ...(wasSkipped && question.skipButtonText ? { skipButtonText: question.skipButtonText } : {}),
                });
            }
        }
    });
});

                setFormState(prev => ({
                    ...prev,
                    currentPanel: panelIndex,
                    currentQuestion: questionIndex,
                    lastUnanswered: { panelIndex, questionIndex },
                    submittedAnswers: answers,
                    seenQuestions: seenQuestionTracking
                }));

                hasInitializedRef.current = formKey;
                setIsInitializing(false);

            } catch (error) {
                console.error("Error initializing form state:", error);
                setIsInitializing(false);
            }
        }

        initializeFormState();
    }, [currentFormData?.key, panels, panelKeys]);

    // Prefill form values when the user navigates to a different question.
    // The guard below prevents this effect from firing on spurious re-renders
    // where currentQuestion gets a new object reference but the actual question
    // position hasn't changed (e.g. after a fetchPatientData or debounced queue
    // update). Only a genuine question transition or new submission should
    // trigger reset() + setValue.
    useEffect(() => {
        if (!formState.isEditing && currentQuestion) {
            const prefillKey = `${formState.currentPanel}_${formState.currentQuestion}_${formState.submittedAnswers.length}`;
            if (prevPrefillKeyRef.current === prefillKey) {
                return;
            }
            prevPrefillKeyRef.current = prefillKey;

            reset();
    
            // Look for existing answer in submittedAnswers for this question
            const existingEntry = formState.submittedAnswers.find(
                entry => entry.questionVarName === currentQuestion.questionVarName
            );
    
            if (existingEntry) {
                // Found existing answers, prefill the form
                existingEntry.answers.forEach((answer, idx) => {
                    const subquestionIndex = idx + 1;
                    const subquestion = currentQuestion.subquestions?.[subquestionIndex];
                    if (!subquestion) return;

                    const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${subquestionIndex}`;

                    // Handle different question types
                    if (subquestion.inputType === "selectMultiple") {
                        if (answer.answer && answer.answer !== "No answer given") {
                            // Handle arrays stored in Firestore — normalizeSelectMultipleSelections
                            // recursively unwraps any nested {value: {value: ...}} corruption and
                            // deduplicates, producing a clean string array for the form control.
                            if (Array.isArray(answer.answer)) {
                                const selections = normalizeSelectMultipleSelections(answer.answer);
                                setValue(answerKey, selections);

                                // Set any "other" values — unwrap each entry to reach the leaf
                                // value for the otherOptions key lookup, while reading otherValue
                                // from the outermost level where it is stored.
                                answer.answer.forEach(a => {
                                    const val = unwrapNestedValue(a);
                                    const otherVal = a.otherValue;
                                    if (otherVal && subquestion.otherOptions && typeof val === 'string' && val in subquestion.otherOptions) {
                                        const otherKey = otherOptionFieldKey(answerKey, val);
                                        setValue(otherKey, otherVal);
                                        setFormState(prev => ({ ...prev, showOtherInput: true }));
                                    }
                                });
                            }
                            // Handle legacy string format
                            else if (typeof answer.answer === 'string') {
                                const selections = answer.answer.split(', ').map(selection => {
                                    const [value, otherValue] = selection.split(': ');
                                    if (otherValue && subquestion.otherOptions && value in subquestion.otherOptions) {
                                        const otherKey = otherOptionFieldKey(answerKey, value);
                                        setValue(otherKey, otherValue);
                                        setFormState(prev => ({ ...prev, showOtherInput: true }));
                                    }
                                    return value;
                                });
                                setValue(answerKey, selections);
                            }
                        }
                    } else if (subquestion.inputType === "selectOne") {
                        if (answer.answer && answer.answer !== "No answer given") {
                            // Handle object stored in Firestore
                            if (typeof answer.answer === 'object' && answer.answer !== null) {
                                setValue(answerKey, answer.answer.value);

                                if (answer.answer.otherValue && subquestion.otherOptions &&
                                    answer.answer.value in subquestion.otherOptions) {
                                    const otherKey = otherOptionFieldKey(answerKey, answer.answer.value);
                                    setValue(otherKey, answer.answer.otherValue);
                                    setFormState(prev => ({ ...prev, showOtherInput: true }));
                                }
                            }
                            // Handle legacy string format
                            else if (typeof answer.answer === 'string') {
                                const [selectedValue, otherValue] = answer.answer.split(': ');
                                setValue(answerKey, selectedValue);
                                if (otherValue && subquestion.otherOptions && selectedValue in subquestion.otherOptions) {
                                    const otherKey = otherOptionFieldKey(answerKey, selectedValue);
                                    setValue(otherKey, otherValue);
                                    setFormState(prev => ({ ...prev, showOtherInput: true }));
                                }
                            }
                        }
                    } else {
                        // All other input types (text, date, etc.)
                        setValue(answerKey, answer.answer === "No answer given" ? "" : answer.answer);
                    }
                });
            }
            // If no existing entry but there are prefilled values from provider, use those
        else if (currentQuestion.subquestions) {
            Object.entries(currentQuestion.subquestions).forEach(([subquestionIndex, subquestion]) => {
                if (subquestion.answer !== undefined && subquestion.answer !== null) {
                    const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${subquestionIndex}`;
                    
                    // Handle different input types — for selectMultiple, normalize
                    // to clean strings so the form control never holds raw Firestore objects.
                    if (subquestion.inputType === "selectMultiple" && Array.isArray(subquestion.answer)) {
                        setValue(answerKey, normalizeSelectMultipleSelections(subquestion.answer));
                    } else if (subquestion.inputType === "selectOne") {
                        // Handle object stored in Firestore
                        if (typeof subquestion.answer === 'object' && subquestion.answer !== null) {
                            setValue(answerKey, subquestion.answer.value);

                            if (subquestion.answer.otherValue && subquestion.otherOptions &&
                                subquestion.answer.value in subquestion.otherOptions) {
                                const otherKey = otherOptionFieldKey(answerKey, subquestion.answer.value);
                                setValue(otherKey, subquestion.answer.otherValue);
                                setFormState(prev => ({ ...prev, showOtherInput: true }));
                            }
                        }
                        // Handle legacy string format
                        else if (typeof subquestion.answer === 'string') {
                            setValue(answerKey, subquestion.answer);
                        }
                    } else {
                        setValue(answerKey, subquestion.answer);
                    }
                }
            });
        }
    }
    }, [formState.currentPanel, formState.currentQuestion, formState.isEditing, formState.submittedAnswers, currentQuestion, reset, setValue]);

    // Form locking decision — see formLockingUtils.js for the full business-rule
    // documentation.  The logic is extracted into a pure function so it can be
    // covered by unit tests independently of the React component lifecycle.
    useEffect(() => {
        setIsFormLocked(computeIsFormLocked(patientDataModel));
    }, [patientDataModel]);

    useEffect(() => {
        // Only show confetti when:
        // - progress is 100% (can be driven by fresh/server/local sources), AND
        // - the user has actually submitted the final question at least once,
        // - and we haven't already shown confetti in this session.
        //
        // This ensures provider prefill can count toward completion without
        // surprising the user with confetti before they review/confirm the last item.
        if (progress === 100 && hasSubmittedFinalQuestionOnce && !formState.showConfetti) {
            setFormState(prev => ({ ...prev, showConfetti: true }));
        }
    }, [progress, hasSubmittedFinalQuestionOnce, formState.showConfetti]);


    // Process and save answers
    const processFormAnswers = (data, question, isSkipping = false) => {
        if (!question?.subquestions) return [];

        // If we're skipping, return empty answers (null values)
        if (isSkipping) {
            return Object.entries(question.subquestions).map(([subquestionIndex, subquestion]) => {
                return {
                    question: subquestion.subquestionDisplayText,
                    answer: null,  // Keep as null to maintain data integrity
                    displayAnswer: question.skipButtonText || "I'm unable to provide this information", // This gets overridden later
                    subquestionIndex
                };
            });
        }

        return Object.entries(question.subquestions).map(([subquestionIndex, subquestion]) => {
            const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${subquestionIndex}`;
            let finalAnswer;
            let displayAnswer;

            if (subquestion.inputType === "selectMultiple") {
                const answer = data[answerKey];
                if (!answer || (Array.isArray(answer) && answer.length === 0)) {
                    finalAnswer = null;
                    displayAnswer = "No answer given";
                } else {
                    const selectedAnswers = Array.isArray(answer) ? answer : [answer];

                    // Unwrap each answer to its leaf string before wrapping in the
                    // {value, otherValue?} save format. This prevents re-nesting if a
                    // corrupted object somehow ends up in the form value array.
                    const formattedAnswers = selectedAnswers.map(a => {
                        const normalized = unwrapNestedValue(a);
                        if (subquestion.otherOptions && typeof normalized === 'string' && normalized in subquestion.otherOptions) {
                            const otherKey = otherOptionFieldKey(answerKey, normalized);
                            const otherAnswer = data[otherKey];
                            return otherAnswer ? { value: normalized, otherValue: otherAnswer } : { value: normalized };
                        }
                        return { value: normalized };
                    });

                    finalAnswer = formattedAnswers.length > 0 ? formattedAnswers : null;

                    // For UI display: Format as comma-separated string
                    displayAnswer = formattedAnswers.length > 0
                        ? formattedAnswers.map(a => a.otherValue ? `${a.value}: ${a.otherValue}` : a.value).join(', ')
                        : "No answer given";
                }
            } else if (subquestion.inputType === "selectOne") {
                const answer = data[answerKey];
                if (!answer) {
                    finalAnswer = null;
                    displayAnswer = "No answer given";
                } else {
                    // Check if this option has an "other" input
                    if (subquestion.otherOptions && answer in subquestion.otherOptions) {
                        const otherKey = otherOptionFieldKey(answerKey, answer);
                        const otherAnswer = data[otherKey];
                        finalAnswer = otherAnswer ? { value: answer, otherValue: otherAnswer } : { value: answer };
                        displayAnswer = otherAnswer ? `${answer}: ${otherAnswer}` : answer;
                    } else {
                        finalAnswer = { value: answer };
                        displayAnswer = answer;
                    }
                }
            } else {
                // Handle other input types as before
                const answer = data[answerKey];
                finalAnswer = answer || null;
                displayAnswer = answer || "No answer given";
            }

            return {
                question: subquestion.subquestionDisplayText,
                answer: finalAnswer,
                displayAnswer: displayAnswer,
                subquestionIndex,
                serviceUserVarName: subquestion.serviceUserVarName || null,
            };
        });
    };

    const saveToFirebase = async (data, isSkipping = false, customNextState = null) => {
        console.log("Starting to save to Firebase")
        console.log("isSkipping", isSkipping)

        const user = auth.currentUser;
        if (!user || !currentFormData?.key) {
            throw new Error("Cannot save - missing user or form data");
        }

        try {
            const processedAnswers = processFormAnswers(data, currentQuestion, isSkipping);
            let formNewlyCompleted = false;
            let latestProgressPct = null;

            // Clear answers for subquestions that are no longer applicable due to a gating
            // answer change. We build a temporary lookup reflecting the just-submitted answers
            // and normalise them with extractPrimaryValue so that isSubquestionVisible can
            // compare against the string values in conditionsToShow (e.g. "No", "Never").
            // Raw answers are objects like {value: "No"} which would fail string comparison.
            // This must happen BEFORE the loop that builds updatedAnswers / Firestore paths
            // so that cleared values flow through to the persistence layer and prefill.
            if (!isSkipping) {
                const tempLookup = {};
                processedAnswers.forEach(({ answer, subquestionIndex }) => {
                    const sq = currentQuestion.subquestions[subquestionIndex];
                    if (sq?.serviceUserVarName) {
                        tempLookup[sq.serviceUserVarName] = extractPrimaryValue(answer);
                    }
                });

                processedAnswers.forEach((entry) => {
                    const sq = currentQuestion.subquestions[entry.subquestionIndex];
                    if (sq?.conditionsToShow && !isSubquestionVisible(sq, tempLookup)) {
                        entry.answer = null;
                        entry.displayAnswer = "No answer given";
                    }
                });
            }

            await runTransaction(db, async (transaction) => {
                const patientRef = doc(db, 'patients', user.uid);
                const patientDoc = await transaction.get(patientRef);

                if (!patientDoc.exists()) {
                    throw new Error("Patient document does not exist!");
                }

                const formKey = currentFormData.key;
                const existingData = patientDoc.data();
                const currentFormState = existingData.formData?.[formKey] || {
                    lastUpdated: null,
                    formSubsections: {}
                };

                // Build updates object and collect all updated answers
                const updates = {};
                const updatedAnswers = {};

                // First pass: update formData and collect fresh answers for dataModel update
                processedAnswers.forEach(({ answer, subquestionIndex }) => {
                    // Store the updated answer for dataModel updates later
                    const subquestion = currentQuestion.subquestions[subquestionIndex];
                    updatedAnswers[subquestion.serviceUserVarName] = answer;

                    // IMPORTANT: Always update the formData, even with null or empty values
                    // This ensures edits that clear previous answers are properly saved
                    // Update the formData as before
                    updates[`formData.${formKey}.formSubsections.${formState.currentPanel}.questions.${formState.currentQuestion}.subquestions.${subquestionIndex}`] = {
                        ...currentQuestion.subquestions[subquestionIndex],
                        answer
                    };
                });

                // Build an enhanced, in-memory copy of the current form that includes the
                // just-submitted answers. We will use this enhanced object as the single
                // source of truth to recompute progress (total and answered) on each save.
                const enhancedFormDataForProgress = buildEnhancedFormWithSubmission(
                    existingData.formData?.[formKey],
                    currentQuestion,
                    updatedAnswers,
                    formState,
                    isSkipping
                );

                // Check if this is a standardized scale and calculate the score if it's complete
                if (isScorableScale(formKey)) {
                    console.log(`[PatientFormTemplate] ${formKey} is a scorable scale - preparing to calculate score`);

                    // Create an enhanced version of the form data with the latest answers
                    const enhancedFormData = enhancedFormDataForProgress;

                    console.log(`[PatientFormTemplate] Enhanced form data before scoring:`,
                        JSON.stringify(enhancedFormData, null, 2).substring(0, 500) + '...');

                    // Extract patient data for scales that need it (like BDEFS)
                    let patientData = null;
                    if (formKey === 'bdefs') {
                        console.log(`[PatientFormTemplate] Extracting patient data for BDEFS scoring`);
                        
                        // Get patient data from yourInformationSubclinical form
                        const yourInfoForm = existingData.formData?.yourInformationSubclinical;
                        if (yourInfoForm) {
                            // Extract date of birth
                            const dobAnswer = yourInfoForm.formSubsections?.['1']?.questions?.['2']?.subquestions?.['1']?.answer;
                            // Extract sex assigned at birth  
                            const sexAnswer = yourInfoForm.formSubsections?.['1']?.questions?.['3']?.subquestions?.['1']?.answer;
                            
                            if (dobAnswer && sexAnswer) {
                                patientData = {
                                    dateOfBirth: dobAnswer,
                                    sexAssignedAtBirth: typeof sexAnswer === 'object' ? sexAnswer.value : sexAnswer
                                };
                                console.log(`[PatientFormTemplate] Patient data extracted:`, patientData);
                            } else {
                                console.log(`[PatientFormTemplate] Missing patient data - DOB: ${!!dobAnswer}, Sex: ${!!sexAnswer}`);
                            }
                        } else {
                            console.log(`[PatientFormTemplate] yourInformationSubclinical form not found`);
                        }
                    }

                    // Calculate the scale score
                    console.log(`[PatientFormTemplate] Calling scoreScale for ${formKey}`);
                    const scaleResults = await scoreScale(formKey, enhancedFormData, patientData);
                    console.log(`[PatientFormTemplate] Scale results for ${formKey}:`, scaleResults);

                    // Only save the score if the scale is complete
                    if (scaleResults && scaleResults.complete === true) {
                        console.log(`[PatientFormTemplate] Scale ${formKey} is complete, saving results`);
                        updates[`formData.${formKey}.scaleResults`] = {
                            ...scaleResults,
                            lastUpdated: serverTimestamp(),
                        };
                    } else {
                        console.log(`[PatientFormTemplate] Scale ${formKey} is incomplete or returned no results`);
                    }
                } else {
                    console.log(`[PatientFormTemplate] ${formKey} is not a scorable scale`);
                }

                // Second pass: update dataModel with the freshest data
                processedAnswers.forEach(({ answer, subquestionIndex }) => {
                    const subquestion = currentQuestion.subquestions[subquestionIndex];

                    // Check if the parent question has prefill configuration
                    if (currentQuestion.prefillsToModules &&
                        currentQuestion.prefillsToStages &&
                        currentQuestion.prefillsToProviderVarNames) {

                        // For each module this question should prefill
                        currentQuestion.prefillsToModules.forEach((moduleKey, moduleIndex) => {
                            const stageName = currentQuestion.prefillsToStages[moduleIndex];

                            // Handle both array and string provider var names
                            const providerVarNames = Array.isArray(currentQuestion.prefillsToProviderVarNames)
                                ? currentQuestion.prefillsToProviderVarNames
                                : [currentQuestion.prefillsToProviderVarNames];

                            // Process each provider variable name
                            providerVarNames.forEach(providerVarName => {
                                console.log(`Processing prefill for ${moduleKey}, stage: ${stageName}, var: ${providerVarName}`);

                                if (moduleKey && stageName && providerVarName) {
                                    // Find relevant section in dataModel
                                    const moduleData = existingData.dataModel?.[moduleKey] || {};

                                    let sectionKey = null;
                                    Object.entries(moduleData.sections || {}).forEach(([key, section]) => {
                                        if (section.sectionTitle === stageName) {
                                            sectionKey = key;
                                        }
                                    });

                                    if (sectionKey) {
                                        // Create an enhanced version of existingData with the latest form answers
                                        const enhancedExistingData = JSON.parse(JSON.stringify(existingData));

                                        // Patch the existingData with ALL the latest answers from this submission
                                        if (!enhancedExistingData.formData[formKey].formSubsections[formState.currentPanel]) {
                                            enhancedExistingData.formData[formKey].formSubsections[formState.currentPanel] = {
                                                questions: {}
                                            };
                                        }

                                        if (!enhancedExistingData.formData[formKey].formSubsections[formState.currentPanel].questions[formState.currentQuestion]) {
                                            enhancedExistingData.formData[formKey].formSubsections[formState.currentPanel].questions[formState.currentQuestion] = {
                                                subquestions: {}
                                            };
                                        }

                                        // Update all subquestions with fresh data from this submission
                                        Object.entries(currentQuestion.subquestions).forEach(([sqIdx, sq]) => {
                                            if (updatedAnswers[sq.serviceUserVarName] !== undefined) {
                                                enhancedExistingData.formData[formKey].formSubsections[formState.currentPanel].questions[formState.currentQuestion].subquestions[sqIdx] = {
                                                    ...sq,
                                                    answer: updatedAnswers[sq.serviceUserVarName]
                                                };
                                            }
                                        });

                                        // Get all questions in the target subsection
                                        Object.entries(moduleData.sections[sectionKey].subsections || {}).forEach(([subsecKey, subsection]) => {
                                            Object.entries(subsection.subsectionQuestions || {}).forEach(([qKey, question]) => {
                                                if (question.providerVarName === providerVarName) {
                                                    // Get the formatted prefill value
                                                    const { prefillValue } = updateDataModelWithPrefill(
                                                        providerVarName,
                                                        data,
                                                        enhancedExistingData,
                                                        currentFormData,
                                                        formState,
                                                        currentQuestion,
                                                        answer,
                                                        subquestion
                                                    );

                                                    const currentTimestamp = new Date().toISOString();

                                                    // IMPORTANT: Always update prefill values, even when null
                                                    // Set the patientPrefill value and the timestamp of when it was last updated
                                                    updates[`dataModel.${moduleKey}.sections.${sectionKey}.subsections.${subsecKey}.subsectionQuestions.${qKey}.patientPrefill`] = prefillValue;
                                                    updates[`dataModel.${moduleKey}.sections.${sectionKey}.subsections.${subsecKey}.subsectionQuestions.${qKey}.patientPrefillLastUpdatedAt`] = currentTimestamp;
                                                }
                                            });
                                        });
                                    }
                                }
                            });
                        });
                    }
                });


                // If this is a skipped question, just add a flag at the question level
                if (isSkipping) {
                    updates[`formData.${formKey}.formSubsections.${formState.currentPanel}.questions.${formState.currentQuestion}.wasSkipped`] = true;
                }
                else {
                    updates[`formData.${formKey}.formSubsections.${formState.currentPanel}.questions.${formState.currentQuestion}.wasSkipped`] = false;
                }

                // Mark the current question as seen
                updates[`formData.${formKey}.formSubsections.${formState.currentPanel}.questions.${formState.currentQuestion}.hasSeenQuestion`] = true;

                // Calculate next question (use custom state if provided, e.g., for edit mode)
                const nextState = customNextState || getNextQuestionState();
                // Recompute progress from the enhanced form (pure helper)
                const { totalQuestions: clampedTotal, answeredQuestions: clampedAnswered, progressPercentage: clampedPercentage } = recomputeProgress(enhancedFormDataForProgress);

                // Persist progress using recomputed values only (no incremental adjustments)
                const progressUpdate = {
                    lastUnanswered: {
                        panelIndex: nextState.panelIndex,
                        questionIndex: nextState.questionIndex
                    },
                    progressPercentage: clampedPercentage,
                    totalQuestions: clampedTotal,
                    answeredQuestions: clampedAnswered,
                    seenQuestions: {
                        ...(currentFormState.progress?.seenQuestions || {}),
                        [`${formState.currentPanel}_${formState.currentQuestion}`]: true
                    }
                };

                const previousProgress = currentFormState.progress?.progressPercentage || 0;
                const newProgress = progressUpdate.progressPercentage;
                const isFirstTimeCompletion = previousProgress < 100 && newProgress >= 100;
                latestProgressPct = clampedPercentage;

                if (isFirstTimeCompletion && !currentFormState.firstCompletedAt) {
                    console.log(`[PatientFormTemplate] Form ${formKey} newly completed!`);
                    updates[`formData.${formKey}.firstCompletedAt`] = serverTimestamp();
                    formNewlyCompleted = true;
                }

                transaction.update(patientRef, {
                    ...updates,
                    [`formData.${formKey}.lastUpdated`]: serverTimestamp(),
                    [`formData.${formKey}.progress`]: progressUpdate
                });
            });

            // Milestone analytics (fire-and-forget; saves are throttled per form).
            getFormEventContext(currentFormData.key).then((ctx) => {
                if (!ctx) return;
                if (formNewlyCompleted) {
                    emitFormCompleted(ctx, { progressPct: latestProgressPct });
                } else {
                    emitFormSaved(ctx, { progressPct: latestProgressPct });
                }
            });

            // AFTER transaction completes, check if this completion means all forms are done
            // Only do this check if this form was newly completed (performance optimization)
            if (formNewlyCompleted) {
                console.log(`[PatientFormTemplate] Checking if all forms are done...`);
                // Check if all questionnaires are complete
                setTimeout(async () => {
                    const result = await checkAndNotifyAllQuestionnairesComplete(user.uid, currentFormData.key);
                    if (result.allComplete && !result.alreadyNotified) {
                        getFormEventContext().then((ctx) => {
                            if (ctx) emitAllFormsCompleted(ctx);
                        });
                    }
                    if (result.allComplete && !result.alreadyNotified && !result.hasExistingFeedback) {
                        console.log(`[PatientFormTemplate] All forms completed! Showing feedback modal.`);
                        setShowFeedbackModal(true);
                    }
                }, 0);
            }

            // Fire-and-forget: if the patient changed their first or last name
            // on the "Your Name" question, trigger the updateServiceUserDetails CF
            // to propagate the change to Auth, clinician docs, informant docs,
            // summaries, etc. This runs outside the transaction — form save always
            // succeeds regardless of CF outcome. Each call is idempotent: repeated
            // saves with the same name are no-ops on the server side.
            if (isNameQuestion && nameChangeInfo.hasChanged) {
                const payload = { targetPatientId: user.uid, updates: {} };
                if (watchedFirst != null && watchedFirst !== '' && watchedFirst !== patientDataModel?.firstName) {
                    payload.updates.firstName = watchedFirst;
                }
                if (watchedLast != null && watchedLast !== '' && watchedLast !== patientDataModel?.lastName) {
                    payload.updates.lastName = watchedLast;
                }
                if (Object.keys(payload.updates).length > 0) {
                    httpsCallable(functions, 'updateServiceUserDetails')(payload)
                        .catch(err => console.error('[PatientFormTemplate] Background name sync failed:', err));
                }
            }

            return processedAnswers;
        } catch (error) {
            console.error("Error updating form data:", error);
            // Failed-save analytics: error code only, throttled per form.
            getFormEventContext(currentFormData?.key).then((ctx) => {
                if (ctx) emitFormSaveFailed(ctx, { errorCode: error?.code || error?.name });
            });
            throw error;
        }
    };

    // Build a reconstruction of the form structure from panels + submittedAnswers for
    // visibility-aware answer checking in navigation. Shared by calculateNextUnansweredQuestion
    // and getNextQuestionState to avoid duplicating the reconstruction logic.
    const buildFormSubsectionsFromState = (targetSubmittedAnswers) => {
        const formSubsections = {};
        Object.entries(panels).forEach(([panelIndex, panel]) => {
            const questions = {};
            Object.entries(panel.questions || {}).forEach(([questionIndex, question]) => {
                const entry = targetSubmittedAnswers.find(e =>
                    e.questionVarName === question.questionVarName
                );
                const subquestions = {};
                Object.entries(question.subquestions || {}).forEach(([sqIdx, sq]) => {
                    const answerEntry = entry?.answers?.[parseInt(sqIdx) - 1];
                    subquestions[sqIdx] = {
                        ...sq,
                        answer: answerEntry?.answer ?? sq?.answer ?? null,
                    };
                });
                questions[questionIndex] = {
                    ...question,
                    subquestions,
                    wasSkipped: entry?.wasSkipped ?? question.wasSkipped ?? false,
                };
            });
            formSubsections[panelIndex] = { questions };
        });
        return formSubsections;
    };

    // Determine whether a question is answered in a visibility-aware manner,
    // using the reconstructed form structure and answer lookup.
    const isQuestionAnsweredForNav = (question, reconstructedQ, answerLookup) => {
        if (reconstructedQ?.wasSkipped === true) return true;
        const visibleEntries = getVisibleSubquestionEntries(reconstructedQ, answerLookup);
        if (visibleEntries.length === 0) return true;
        return isQuestionAnswered(reconstructedQ, visibleEntries);
    };

    // Navigation helpers
    const calculateNextUnansweredQuestion = (updatedSubmittedAnswers) => {
        const unansweredSeenQuestions = [];
        const formSubsections = buildFormSubsectionsFromState(updatedSubmittedAnswers);
        const answerLookup = buildAnswerLookup(formSubsections);

        Object.entries(panels).forEach(([panelIndex, panel]) => {
            Object.entries(panel.questions || {}).forEach(([questionIndex, question]) => {
                const panelIdx = parseInt(panelIndex);
                const questionIdx = parseInt(questionIndex);

                const hasSeenQuestion = formState.seenQuestions[`${panelIdx}_${questionIdx}`];
                const reconstructedQ = formSubsections[panelIndex]?.questions?.[questionIndex];
                const hasAnswer = isQuestionAnsweredForNav(question, reconstructedQ, answerLookup);

                if (hasSeenQuestion && !hasAnswer) {
                    unansweredSeenQuestions.push({
                        panelIndex: panelIdx,
                        questionIndex: questionIdx,
                    });
                }
            });
        });

        if (unansweredSeenQuestions.length > 0) {
            return {
                panelIndex: unansweredSeenQuestions[0].panelIndex,
                questionIndex: unansweredSeenQuestions[0].questionIndex
            };
        }

        return {
            panelIndex: formState.lastUnanswered.panelIndex,
            questionIndex: formState.lastUnanswered.questionIndex
        };
    };
    
    const getNextQuestionState = () => {
        // Check if we're at the end of the current panel
        const currentPanelQuestions = Object.keys(panels[formState.currentPanel]?.questions || {}).length;

        if (formState.currentQuestion >= currentPanelQuestions) {
            // At the end of this panel, need to move to the next panel
            const panelKeys = Object.keys(panels).map(k => parseInt(k, 10)).sort((a, b) => a - b);
            const currentPanelIndex = panelKeys.indexOf(formState.currentPanel);

            // If there's a next panel, go to its first question
            if (currentPanelIndex < panelKeys.length - 1) {
                return {
                    panelIndex: panelKeys[currentPanelIndex + 1],
                    questionIndex: 1
                };
            }
            // If we've reached the last question of the last panel
                else {
                const unansweredQuestions = [];
                const formSubsections = buildFormSubsectionsFromState(formState.submittedAnswers);
                const answerLookup = buildAnswerLookup(formSubsections);

                Object.entries(panels).forEach(([panelIndex, panel]) => {
                    Object.entries(panel.questions || {}).forEach(([questionIndex, question]) => {
                        const panelIdx = parseInt(panelIndex);
                        const questionIdx = parseInt(questionIndex);

                        const hasSeenQuestion = formState.seenQuestions[`${panelIdx}_${questionIdx}`];
                        const reconstructedQ = formSubsections[panelIndex]?.questions?.[questionIndex];
                        const hasAnswer = isQuestionAnsweredForNav(question, reconstructedQ, answerLookup);

                        if (hasSeenQuestion && !hasAnswer) {
                            unansweredQuestions.push({
                                panelIndex: panelIdx,
                                questionIndex: questionIdx,
                            });
                        }
                    });
                });

                if (unansweredQuestions.length > 0) {
                    return {
                        panelIndex: unansweredQuestions[0].panelIndex,
                        questionIndex: unansweredQuestions[0].questionIndex
                    };
                }
            }
        } else {
            return {
                panelIndex: formState.currentPanel,
                questionIndex: formState.currentQuestion + 1
            };
        }

        // Default to current state (end of form)
        return {
            panelIndex: formState.currentPanel,
            questionIndex: formState.currentQuestion
        };
    };

    // Dismiss the SLaM crisis-support dialog and let the patient interact
    // with the next question. Navigation has already advanced to Q10 behind
    // the overlay — this callback simply removes the blocking layer.
    const handleCrisisSupportProceed = useCallback(() => {
        setShowCrisisSupportDialog(false);
    }, []);

    // Form submission and navigation
    const handleNextQuestion = async (data, isSkipping = false) => {

        if (isFormLocked) {
            console.log("Form is locked, cannot submit answers");
            return;
        }

        setIsSaving(true);
        try {
            // Capture whether the user is currently on the final question before we save.
            // If so, and the save succeeds, we will consider the final question "confirmed"
            // for this session and allow confetti once progress is 100%.
            const wasOnLastQuestion = isOnLastQuestion;
            // Calculate next state for edit mode if needed
            let customNextState = null;
            if (formState.isEditing) {
                // For edit mode, we need to calculate using the updated answers that will be created
                const tempUpdatedAnswers = [...formState.submittedAnswers];
                const tempProcessedAnswers = processFormAnswers(data, currentQuestion, isSkipping);
                const tempDisplayAnswers = tempProcessedAnswers.map(item => ({
                    question: item.question,
                    answer: item.answer
                }));
                
                tempUpdatedAnswers[formState.editIndex] = {
                    questionTitle: currentQuestion.questionTitle,
                    questionVarName: currentQuestion.questionVarName,
                    answers: tempDisplayAnswers,
                    wasSkipped: isSkipping,
                    ...(isSkipping && currentQuestion.skipButtonText ? { skipButtonText: currentQuestion.skipButtonText } : {}),
                };

                customNextState = calculateNextUnansweredQuestion(tempUpdatedAnswers);
            }

            // Save to Firebase
            const processedAnswers = await saveToFirebase(data, isSkipping, customNextState);
            if (wasOnLastQuestion) {
                setHasSubmittedFinalQuestionOnce(true);
            }
            
            // DEBUG: Log all processed answers
            console.log("DEBUG: All processed answers:", processedAnswers);
            console.log("DEBUG: Current form title:", formTitle);
            console.log("DEBUG: Current question:", currentQuestion);

            // Check for PHQ-9 self-harm response (question 9)
            if (formTitle === "Patient Health Questionnaire (PHQ-9)") {
                console.log("DEBUG: PHQ-9 form detected");
                
                if (currentQuestion) {
                    console.log("DEBUG: Current question varName:", currentQuestion.questionVarName);
                } else {
                    console.log("DEBUG: Current question is null");
                }
                
                if (!isSkipping) {
                    console.log("DEBUG: Question was not skipped");
                } else {
                    console.log("DEBUG: Question was skipped, skipping self-harm check");
                }
            }
            
            // Whether a crisis-support dialog should be shown after navigation
            // advances. Set to true only for SLaM / 3 Borough patients who
            // select a concerning answer on PHQ-9 Q9.
            let shouldShowCrisisDialog = false;

            // The three PHQ-9 Q9 answers that indicate potential self-harm —
            // the same list used by checkAndNotifySelfHarmConcern on the
            // backend. "Not at all" is the only non-concerning option.
            const CONCERNING_ANSWERS = ['Several days', 'More than half the days', 'Nearly every day'];

            if (formTitle === "Patient Health Questionnaire (PHQ-9)" && 
                currentQuestion && 
                currentQuestion.questionVarName === "phq9_question9" && 
                !isSkipping) {
                
                console.log("DEBUG: PHQ-9 question 9 (self-harm) detected");
                
                // Extract the answer value
                const selfHarmAnswer = processedAnswers.find(answer => 
                    answer.question === "How often have you been bothered by this over the last two weeks?"
                );
                
                console.log("DEBUG: Self-harm answer found:", selfHarmAnswer);
                
                if (selfHarmAnswer) {
                    console.log("DEBUG: Self-harm answer display value:", selfHarmAnswer.displayAnswer);
                    console.log("DEBUG: Self-harm answer raw value:", selfHarmAnswer.answer);
                }
                
                if (selfHarmAnswer && selfHarmAnswer.displayAnswer) {
                    console.log("DEBUG: Valid self-harm answer detected:", selfHarmAnswer.displayAnswer);
                    
                    const user = auth.currentUser;
                    if (user) {
                        console.log("DEBUG: User authenticated, attempting to send notification");
                        // Send concern notification if needed
                        checkAndNotifySelfHarmConcern(
                            user.uid, 
                            selfHarmAnswer.displayAnswer,
                            currentQuestion.questionTitle
                        ).then(result => {
                            console.log("DEBUG: Self-harm check result:", result);
                            if (result.concernDetected) {
                                console.log("Self-harm concern notification triggered");
                            } else {
                                console.log("DEBUG: No concern detected or already notified");
                            }
                        }).catch(err => {
                            console.error("Error checking self-harm concern:", err);
                        });
                    } else {
                        console.log("DEBUG: No authenticated user found");
                    }

                    // Show the on-screen crisis-support dialog for SLaM / 3
                    // Borough patients. The dialog blocks interaction with the
                    // next question until the patient clicks "Proceed to next
                    // question". The email notification fires independently
                    // above (fire-and-forget) — the dialog is purely
                    // presentational and appears regardless of email cooldown.
                    //
                    // Gated on isSlamNhsOrg (NHS trusts only), NOT isSlamOrg:
                    // the dialog copy names SLaM services and the slam.nhs.uk
                    // crisis line, so a private org that merely shares the
                    // SLaM clinical workflow (e.g. Urban ADHD) must not see it.
                    if (CONCERNING_ANSWERS.includes(selfHarmAnswer.displayAnswer)
                        && isSlamNhsOrg(patientDataModel?.organisation)) {
                        shouldShowCrisisDialog = true;
                    }
                } else {
                    console.log("DEBUG: Self-harm answer not found or empty");
                }
            }

            // Prepare answers for display based on skipped state
            let updatedAnswers;

            if (isSkipping) {
                const skipText = currentQuestion.skipButtonText || "I'm unable to provide this information";
                updatedAnswers = currentQuestion.subquestions ?
                    Object.values(currentQuestion.subquestions).map(subq => ({
                        question: subq.subquestionDisplayText,
                        answer: skipText,
                        serviceUserVarName: subq.serviceUserVarName || null,
                    })) : [];
            } else {
                // Pass the structured answer (finalAnswer) so FormAnswerSummary
                // can render bullets and italic curly-quoted free text immediately,
                // matching what it shows after Firestore rehydration.
                updatedAnswers = processedAnswers.map(item => ({
                    question: item.question,
                    answer: item.answer,
                    serviceUserVarName: item.serviceUserVarName || null,
                }));
            }

            // Update state based on whether editing or continuing
            if (formState.isEditing) {
                // Create a new copy of submittedAnswers to avoid reference issues
                const newSubmittedAnswers = [...formState.submittedAnswers];
                newSubmittedAnswers[formState.editIndex] = {
                    questionTitle: currentQuestion.questionTitle,
                    questionVarName: currentQuestion.questionVarName,
                    answers: [...updatedAnswers],
                    wasSkipped: isSkipping,
                    ...(isSkipping && currentQuestion.skipButtonText ? { skipButtonText: currentQuestion.skipButtonText } : {}),
                };

                // Calculate next unanswered question using the updated submittedAnswers
                const nextState = calculateNextUnansweredQuestion(newSubmittedAnswers);

                // Update state for editing - navigate to next unanswered question
                setFormState(prev => ({
                    ...prev,
                    submittedAnswers: newSubmittedAnswers,
                    isEditing: false,
                    editIndex: null,
                    currentPanel: nextState.panelIndex,
                    currentQuestion: nextState.questionIndex,
                    lastUnanswered: {
                        panelIndex: nextState.panelIndex,
                        questionIndex: nextState.questionIndex
                    },
                    showOtherInput: false,
                    // Track that this question has been seen
                    seenQuestions: {
                        ...prev.seenQuestions,
                        [`${formState.currentPanel}_${formState.currentQuestion}`]: true
                    }
                }));
            } else {
                // Check if this question is already in submittedAnswers
                const existingEntryIndex = formState.submittedAnswers.findIndex(
                    entry => entry.questionVarName === currentQuestion.questionVarName
                );

                let newSubmittedAnswers;

                if (existingEntryIndex >= 0) {
                    // Update existing entry rather than adding a new one
                    newSubmittedAnswers = [...formState.submittedAnswers];
                    newSubmittedAnswers[existingEntryIndex] = {
                        questionTitle: currentQuestion.questionTitle,
                        questionVarName: currentQuestion.questionVarName,
                        answers: [...updatedAnswers],
                        wasSkipped: isSkipping,
                        ...(isSkipping && currentQuestion.skipButtonText ? { skipButtonText: currentQuestion.skipButtonText } : {}),
                    };
                } else {
                    // Add as a new entry
                    newSubmittedAnswers = [
                        ...formState.submittedAnswers,
                        {
                            questionTitle: currentQuestion.questionTitle,
                            questionVarName: currentQuestion.questionVarName,
                            answers: [...updatedAnswers],
                            wasSkipped: isSkipping,
                            ...(isSkipping && currentQuestion.skipButtonText ? { skipButtonText: currentQuestion.skipButtonText } : {}),
                        }
                    ];
                }

                // Move to next question
                const nextState = getNextQuestionState();

                // Update state for continuing
                setFormState(prev => ({
                    ...prev,
                    submittedAnswers: newSubmittedAnswers,
                    lastUnanswered: {
                        panelIndex: nextState.panelIndex,
                        questionIndex: nextState.questionIndex
                    },
                    currentPanel: nextState.panelIndex,
                    currentQuestion: nextState.questionIndex,
                    showOtherInput: false,
                    // Track that this question has been seen
                    seenQuestions: {
                        ...prev.seenQuestions,
                        [`${formState.currentPanel}_${formState.currentQuestion}`]: true
                    },
                }));
            }

            // Show the crisis-support alert dialog for SLaM / 3 Borough
            // patients who gave a concerning PHQ-9 Q9 answer. The dialog
            // renders as a blocking overlay on top of the next question —
            // navigation has already advanced but the Radix AlertDialog
            // traps focus and prevents interaction until the patient clicks
            // "Proceed to next question".
            if (shouldShowCrisisDialog) {
                setShowCrisisSupportDialog(true);
            }

            // Reset form
            reset();

            // Scroll to form top including navigation buttons
            scrollToFormTop();

        } catch (error) {
            console.error("Error handling question submission:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = async (index) => {
        // If form is locked, prevent editing
        if (isFormLocked) {
            console.log("Form is locked, cannot edit answers");
            return;
        }

        try {
            const entry = formState.submittedAnswers[index];
            let targetPanel = 1;
            let targetQuestion = 1;

            // Find the panel and question index by questionVarName
            Object.entries(panels).forEach(([pKey, panel]) => {
                Object.entries(panel.questions || {}).forEach(([qKey, question]) => {
                    if (question.questionVarName === entry.questionVarName) {
                        targetPanel = parseInt(pKey, 10);
                        targetQuestion = parseInt(qKey, 10);
                    }
                });
            });

            if (targetPanel && targetQuestion) {
                // Fetch fresh data from Firestore before editing
                const user = auth.currentUser;
                if (user && currentFormData?.key) {
                    const patientRef = doc(db, 'patients', user.uid);
                    const patientDoc = await getDoc(patientRef);

                    if (patientDoc.exists()) {
                        // Get the latest data for this question
                        const formData = patientDoc.data().formData?.[currentFormData.key];
                        const questionData = formData?.formSubsections?.[targetPanel]?.questions?.[targetQuestion];

                        if (questionData) {
                            // Use fresh answers from Firestore
                            const freshAnswers = Object.entries(questionData.subquestions || {}).map(([subIdx, subq]) => ({
                                question: subq.subquestionDisplayText,
                                answer: subq.answer ?? "No answer given",
                                serviceUserVarName: subq.serviceUserVarName || null,
                            }));

                            // Update the entry with fresh data
                            entry.answers = freshAnswers;
                        }
                    }
                }

                // Clear form before prefilling
                reset();

                // Prefill the form with fresh data
                entry.answers.forEach((answer, idx) => {
                    const subquestion = panels[targetPanel].questions[targetQuestion]?.subquestions?.[idx + 1];
                    if (!subquestion) return;

                    const answerKey = `question_${targetPanel}_${targetQuestion}_${idx + 1}`;

                    // Skip if no answer or "No answer given"
                    if (!answer.answer || answer.answer === "No answer given") return;

                    // Handle different question types
                    if (subquestion.inputType === "selectMultiple") {
                        // Handle arrays stored in Firestore — normalize to clean strings
                        // (same approach as the prefill useEffect)
                        if (Array.isArray(answer.answer)) {
                            const selections = normalizeSelectMultipleSelections(answer.answer);
                            setValue(answerKey, selections);

                            // Set any "other" values — unwrap each entry to reach the leaf
                            answer.answer.forEach(a => {
                                const val = unwrapNestedValue(a);
                                const otherVal = a.otherValue;
                                if (otherVal && subquestion.otherOptions && typeof val === 'string' && val in subquestion.otherOptions) {
                                    const otherKey = otherOptionFieldKey(answerKey, val);
                                    setValue(otherKey, otherVal);
                                    setFormState(prev => ({ ...prev, showOtherInput: true }));
                                }
                            });
                        }
                        // Handle legacy string format (for backward compatibility)
                        else if (typeof answer.answer === 'string') {
                            const selections = answer.answer.split(', ').map(selection => {
                                const [value, otherValue] = selection.split(': ');
                                if (otherValue && subquestion.otherOptions && value in subquestion.otherOptions) {
                                    const otherKey = otherOptionFieldKey(answerKey, value);
                                    setValue(otherKey, otherValue);
                                    setFormState(prev => ({ ...prev, showOtherInput: true }));
                                }
                                return value;
                            });
                            setValue(answerKey, selections);
                        }
                    } else if (subquestion.inputType === "selectOne") {
                        // Handle object stored in Firestore
                        if (typeof answer.answer === 'object' && answer.answer !== null) {
                            setValue(answerKey, answer.answer.value);

                            if (answer.answer.otherValue && subquestion.otherOptions &&
                                answer.answer.value in subquestion.otherOptions) {
                                const otherKey = otherOptionFieldKey(answerKey, answer.answer.value);
                                setValue(otherKey, answer.answer.otherValue);
                                setFormState(prev => ({ ...prev, showOtherInput: true }));
                            }
                        }
                        // Handle legacy string format (for backward compatibility)
                        else if (typeof answer.answer === 'string') {
                            const [selectedValue, otherValue] = answer.answer.split(': ');
                            setValue(answerKey, selectedValue);
                            if (otherValue && subquestion.otherOptions && selectedValue in subquestion.otherOptions) {
                                const otherKey = otherOptionFieldKey(answerKey, selectedValue);
                                setValue(otherKey, otherValue);
                                setFormState(prev => ({ ...prev, showOtherInput: true }));
                            }
                        }
                    } else {
                        setValue(answerKey, answer.answer);
                    }
                });

                // Update state to edit mode
                setFormState(prev => ({
                    ...prev,
                    currentPanel: targetPanel,
                    currentQuestion: targetQuestion,
                    isEditing: true,
                    editIndex: index,
                    originalAnswers: [...entry.answers], // Make a deep copy
                    showOtherInput: entry.answers.some(a =>
                        (typeof a.answer === 'string' && a.answer?.includes(': ')) ||
                        (typeof a.answer === 'object' && a.answer?.otherValue)
                    )
                }));

                // Scroll to the subsection title so the user sees the full
                // question context (title + question number) from the top. Uses a
                // longer delay than normal navigation to let React render the
                // pre-filled question before scrolling. scrollToFormTop targets
                // #form-section-heading and falls back to the question heading.
                setTimeout(() => {
                    const sectionHeading = document.getElementById('form-section-heading');
                    if (sectionHeading) {
                        sectionHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                        const heading = questionHeadingRef.current;
                        if (heading) {
                            heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                            scrollToFormTop();
                        }
                    }
                }, 150);
            }
        } catch (error) {
            console.error("Error loading question for editing:", error);
        }
    };

    const handleCancelEdit = () => {
        reset();
        setFormState(prev => ({
            ...prev,
            isEditing: false,
            editIndex: null,
            originalAnswers: null,
            currentPanel: prev.lastUnanswered.panelIndex,
            currentQuestion: prev.lastUnanswered.questionIndex,
            showOtherInput: false
        }));

        // Scroll to form top including navigation buttons
        scrollToFormTop();
    };

    const handleSaveAndGoBack = async () => {
        try {
            // Get current form values
            const currentFormValues = watch();
            
            // Check if there are any values to save
            const hasFormValues = currentQuestion?.subquestions && Object.entries(currentQuestion.subquestions).some(([subquestionIndex]) => {
                const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${subquestionIndex}`;
                const value = currentFormValues[answerKey];
                
                if (Array.isArray(value)) {
                    return value.length > 0;
                } else if (typeof value === 'string') {
                    return value.trim() !== '';
                }
                
                return value !== undefined && value !== null;
            });

            // If there are form values, save them before navigating
            if (hasFormValues && !isFormLocked) {
                console.log("Saving form data before navigation...");
                const wasOnLastQuestion = isOnLastQuestion;
                // saveToFirebase returns processedAnswers with hidden-answer clearing
                // already applied. Reuse it for the local state update so submittedAnswers
                // stays consistent with what was persisted to Firestore.
                const processedAnswers = await saveToFirebase(currentFormValues, false);
                if (wasOnLastQuestion) {
                    setHasSubmittedFinalQuestionOnce(true);
                }
                
                const updatedAnswers = processedAnswers.map(item => ({
                    question: item.question,
                    answer: item.answer,
                    serviceUserVarName: item.serviceUserVarName || null,
                }));

                // Check if this question is already in submittedAnswers
                const existingEntryIndex = formState.submittedAnswers.findIndex(
                    entry => entry.questionVarName === currentQuestion.questionVarName
                );

                let newSubmittedAnswers;
                if (existingEntryIndex >= 0) {
                    // Update existing entry
                    newSubmittedAnswers = [...formState.submittedAnswers];
                    newSubmittedAnswers[existingEntryIndex] = {
                        questionTitle: currentQuestion.questionTitle,
                        questionVarName: currentQuestion.questionVarName,
                        answers: [...updatedAnswers],
                        wasSkipped: false
                    };
                } else {
                    // Add as new entry
                    newSubmittedAnswers = [
                        ...formState.submittedAnswers,
                        {
                            questionTitle: currentQuestion.questionTitle,
                            questionVarName: currentQuestion.questionVarName,
                            answers: [...updatedAnswers],
                            wasSkipped: false
                        }
                    ];
                }

                // Update state with saved answers
                setFormState(prev => ({
                    ...prev,
                    submittedAnswers: newSubmittedAnswers,
                    seenQuestions: {
                        ...prev.seenQuestions,
                        [`${formState.currentPanel}_${formState.currentQuestion}`]: true
                    }
                }));
            }

            // Navigate to dashboard
            const dashboardPath = userType === 'Informant' ? '/informant-dashboard' : '/patient-dashboard';
            navigate(dashboardPath);
            
        } catch (error) {
            console.error("Error saving form data before navigation:", error);
            // Still navigate even if save fails to prevent user from being stuck
            const dashboardPath = userType === 'Informant' ? '/informant-dashboard' : '/patient-dashboard';
            navigate(dashboardPath);
        }
    };

    // Render form
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Screen-reader-only live region for announcing question transitions */}
            <div
                ref={liveRegionRef}
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            />

            {/* Banner Image with Gradient Overlay */}
            <div className="h-10 md:h-40 w-full relative">
                <div className="absolute inset-0 bg-gradient-to-r from-pink-200 via-purple-200 to-blue-200 opacity-80"></div>
            </div>

            {/* Profile Info */}
            <div className="px-4">
                <div className="text-left mt-4 p-2 md:p-4">
                    <h1 className="text-2xl md:text-4xl font-bold text-gray-900">{formTitle}</h1>
                </div>

                {/* Skeleton: progress bar placeholder — fades out when initialization completes */}
                <div className={`mt-4 mb-2 transition-opacity duration-300 ease-in-out ${
                    isInitializing ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
                }`}>
                    <div className="h-3 w-full rounded-full bg-gray-200 animate-pulse" />
                </div>

                {/* Real content: navigation + progress bar — fades in once initialised */}
                {!isInitializing && (
                    <div className="animate-fadeIn">
                        {/* Navigation Buttons */}
                        <FormNavigation
                            navigate={navigate}
                            shouldShowCompletionCard={shouldShowCompletionCard}
                            isFormLocked={isFormLocked}
                            isEditing={formState.isEditing}
                            userType={userType}
                            handleSaveAndGoBack={handleSaveAndGoBack}
                        />

                        {/* Progress Bar — hidden in Focus Mode */}
                        <div className="a11y-focus-hide">
                          <FormProgressBar
                              progress={progress}
                              showConfetti={formState.showConfetti}
                          />
                        </div>
                    </div>
                )}
            </div>

            <div className="flex-grow px-6 md:px-8">
                {/* Skeleton: question card placeholder — fades out when initialization completes */}
                <div className={`transition-opacity duration-300 ease-in-out ${
                    isInitializing ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
                }`}>
                    <div className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-6 shadow-md mb-6 animate-pulse">
                        <div className="h-5 w-3/4 rounded bg-gray-200 mb-4" />
                        <div className="space-y-3">
                            <div className="h-4 w-full rounded bg-gray-200" />
                            <div className="h-4 w-5/6 rounded bg-gray-200" />
                            <div className="h-4 w-2/3 rounded bg-gray-200" />
                        </div>
                        <div className="mt-6 h-10 w-40 rounded-lg bg-gray-200" />
                    </div>
                </div>

                {/* Real content: question card + answer summary — fades in once initialised */}
                {!isInitializing && (
                    <div className="animate-fadeIn">
                        {/* Current Question or Completion Card */}
                        <div className={`${isFormLocked
                                ? 'bg-gray-100'
                                : shouldShowCompletionCard && !formState.isEditing
                                    ? 'bg-green-50'
                                    : 'bg-violet-50'
                                } rounded-2xl border-2 border-violet-300 text-left p-6 shadow-md mb-6`}>
                            {isFormLocked ? (
                                <FormLockedCard />
                            ) : shouldShowCompletionCard && !formState.isEditing ? (
                                <FormCompletionCard />
                            ) : (
                                <>
                                    <FormCurrentQuestionPanel
                                        handleNextQuestion={handleNextQuestion}
                                        handleCancelEdit={handleCancelEdit}
                                        handleSubmit={handleSubmit}
                                        currentPanel={currentPanel}
                                        currentQuestion={currentQuestion}
                                        formState={formState}
                                        register={register}
                                        control={control}
                                        getValues={getValues}
                                        setValue={setValue}
                                        setFormState={setFormState}
                                        totalQuestions={totalQuestions}
                                        currentQuestionNumber={currentQuestionNumber}
                                        formKey={currentFormData?.key}
                                        userOrganisation={patientDataModel?.organisation}
                                        questionHeadingRef={questionHeadingRef}
                                        isSaving={isSaving}
                                        storedAnswerLookup={storedAnswerLookup}
                                        preButtonContent={
                                            /* Name-change warning banner — appears below the form inputs
                                               but above the navigation buttons so the patient sees it
                                               before confirming.  Uses the same max-h/opacity CSS transition
                                               pattern as the Risk Assessment banner for a smooth animated
                                               reveal.  middleName changes do not trigger this banner. */
                                            <div className={`overflow-hidden transition-all duration-400 ease-in-out ${
                                                nameChangeInfo.hasChanged ? 'max-h-24 mt-4 opacity-100' : 'max-h-0 opacity-0'
                                            }`}>
                                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-left">
                                                    <div className="flex items-start">
                                                        <ExclamationIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                                                        <p className="ml-3 text-sm font-semibold text-amber-700">
                                                            By proceeding, your name will be updated on the platform to{' '}
                                                            <span className="font-semibold">{nameChangeInfo.effectiveFirst} {nameChangeInfo.effectiveLast}</span>.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    />
                                </>
                            )}
                        </div>

                        {/* Your Answers Section — hidden in Focus Mode */}
                        <div className="a11y-focus-hide">
                          <FormAnswerSummary
                              formState={formState}
                              handleEdit={handleEdit}
                              isFormLocked={isFormLocked}
                              shouldShowCompletionCard={shouldShowCompletionCard}
                          />
                        </div>
                    </div>
                )}
            </div>
            
            {/* All Forms Completed Feedback Modal */}
            <AllFormsCompletedFeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                onFeedbackSubmitted={() => setShowFeedbackModal(false)}
                // Informants only: their doc names the patient they answer
                // about in createdBy. A patient's createdBy is the clinician
                // who created the record, so this must never be passed for a
                // patient — their own account is already the right subject.
                subjectPatientId={
                    userType === 'Informant' ? patientDataModel?.createdBy || null : null
                }
            />

            {/* SLaM / 3 Borough only: blocking crisis-support dialog shown
                when the patient gives a concerning PHQ-9 Q9 answer. Cannot
                be dismissed by clicking outside — the patient must click
                "Proceed to next question" to continue. */}
            <SelfHarmCrisisSupportDialog
                isOpen={showCrisisSupportDialog}
                onProceed={handleCrisisSupportProceed}
            />
        </div>
    );
}